
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { reviewProfileApi } from "./profile/review-api";
import ReviewDisplay from "./profile/ReviewDisplay";
import ErrorDisplay from "./profile/ErrorDisplay";
import { FileText, Eye } from "lucide-react";

const ProfileReviewer = () => {
  const [profileText, setProfileText] = useState("");
  const [profileReview, setProfileReview] = useState("");
  const [isReviewing, setIsReviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isQuotaExceeded, setIsQuotaExceeded] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [activeTab, setActiveTab] = useState("input");

  const handleReview = async () => {
    if (!profileText.trim()) {
      setError("Please provide your Upwork profile text");
      return;
    }

    setIsReviewing(true);
    setError(null);
    setIsQuotaExceeded(false);
    setIsRateLimited(false);
    setCooldownRemaining(0);

    try {
      const result = await reviewProfileApi(profileText);

      if (result.error) {
        setError(result.error);
        setIsQuotaExceeded(result.isQuotaExceeded);
        setIsRateLimited(result.isRateLimited);
      } else if (result.review) {
        setProfileReview(result.review);
        setActiveTab("result");
      }
    } catch (err: any) {
      setError(err.message || "Failed to review profile. Please try again.");
    } finally {
      setIsReviewing(false);
    }
  };

  const resetForm = () => {
    setProfileText("");
    setProfileReview("");
    setError(null);
    setIsQuotaExceeded(false);
    setIsRateLimited(false);
    setCooldownRemaining(0);
    setActiveTab("input");
  };

  return (
    <Card className="shadow-lg border border-muted/30 overflow-hidden">
      <CardContent className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex justify-center mb-8">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="input" className="data-[state=active]:bg-primary/10">
                <FileText className="h-4 w-4" />
                <span>Input Profile</span>
              </TabsTrigger>
              <TabsTrigger value="result" disabled={!profileReview.length} className="data-[state=active]:bg-primary/10">
                <Eye className="h-4 w-4" />
                <span>Profile Review</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="input" className="mt-0 px-1">
            <div className="bg-muted/5 rounded-xl p-5 border border-muted/20 shadow-sm">
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="profileText" className="flex items-center">
                    Paste your Upwork profile <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Textarea
                    id="profileText"
                    placeholder="Paste your full Upwork profile text here. Include your title, overview, skills, and any other sections you want feedback on."
                    value={profileText}
                    onChange={(e) => setProfileText(e.target.value)}
                    rows={15}
                    required
                    className="min-h-[400px] p-4 text-base border border-muted/30 focus:border-primary/40 transition-colors bg-slate-100 dark:bg-slate-800"
                  />
                  <p className="text-sm text-muted-foreground">
                    For best results, include your entire profile as it appears on Upwork
                  </p>
                </div>

                <ErrorDisplay
                  error={error}
                  isQuotaExceeded={isQuotaExceeded}
                  isRateLimited={isRateLimited}
                  cooldownRemaining={cooldownRemaining}
                />

                <div className="flex justify-end mt-8">
                  <Button
                    onClick={handleReview}
                    disabled={
                      isReviewing ||
                      !profileText.trim() ||
                      isQuotaExceeded ||
                      isRateLimited
                    }
                    className="px-6 py-2.5 text-base font-medium"
                    size="lg"
                  >
                    {isReviewing ? "Reviewing..." : "Review Upwork Profile"}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="result" className="mt-0 px-1">
            <div className="bg-muted/5 rounded-xl p-5 border border-muted/20 shadow-sm">
              <ReviewDisplay
                profileReview={profileReview}
                onReset={resetForm}
              />
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ProfileReviewer;
